import { UserStatus } from '@/types'

/**
 * Background color mappings for UserStatus enum values (for dots, badges, etc.)
 */
export const statusColors: Record<UserStatus, string> = {
  [UserStatus.Online]: 'bg-green-500',
  [UserStatus.OnlineReadyToChat]: 'bg-green-500',
  [UserStatus.OnlineInDeepWork]: 'bg-yellow-500',
  [UserStatus.Offline]: 'bg-gray-400',
  [UserStatus.AwayFromKeyboard]: 'bg-yellow-500',
  [UserStatus.DoNotDisturb]: 'bg-red-500',
  [UserStatus.Busy]: 'bg-orange-500',
}

/**
 * Text color mappings for UserStatus enum values (for status text)
 */
export const statusTextColors: Record<UserStatus, string> = {
  [UserStatus.Online]: 'text-green-600 dark:text-green-400',
  [UserStatus.OnlineReadyToChat]: 'text-green-600 dark:text-green-400',
  [UserStatus.OnlineInDeepWork]: 'text-yellow-600 dark:text-yellow-400',
  [UserStatus.Offline]: 'text-gray-600 dark:text-gray-400',
  [UserStatus.AwayFromKeyboard]: 'text-orange-600 dark:text-orange-400',
  [UserStatus.DoNotDisturb]: 'text-red-600 dark:text-red-400',
  [UserStatus.Busy]: 'text-orange-600 dark:text-orange-400',
}

/**
 * Badge variant mappings for UserStatus enum values
 */
export const statusBadgeVariants: Record<
  UserStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  [UserStatus.Online]: 'default',
  [UserStatus.OnlineReadyToChat]: 'default',
  [UserStatus.OnlineInDeepWork]: 'secondary',
  [UserStatus.Offline]: 'outline',
  [UserStatus.AwayFromKeyboard]: 'secondary',
  [UserStatus.DoNotDisturb]: 'destructive',
  [UserStatus.Busy]: 'destructive',
}

/**
 * Label mappings for UserStatus enum values
 */
export const statusLabels: Record<UserStatus, string> = {
  [UserStatus.Online]: 'Online',
  [UserStatus.OnlineReadyToChat]: 'Online and ready to chat',
  [UserStatus.OnlineInDeepWork]: 'Online and in deep work',
  [UserStatus.Offline]: 'Offline',
  [UserStatus.AwayFromKeyboard]: 'Away from keyboard',
  [UserStatus.DoNotDisturb]: 'Do not disturb',
  [UserStatus.Busy]: 'Busy',
}

/**
 * Short label mappings for UserStatus enum values (for compact displays)
 */
export const statusShortLabels: Record<UserStatus, string> = {
  [UserStatus.Online]: 'Online',
  [UserStatus.OnlineReadyToChat]: 'Ready to chat',
  [UserStatus.OnlineInDeepWork]: 'Deep work',
  [UserStatus.Offline]: 'Offline',
  [UserStatus.AwayFromKeyboard]: 'Away',
  [UserStatus.DoNotDisturb]: 'Do not disturb',
  [UserStatus.Busy]: 'Busy',
}

/**
 * Get all available user status options as an array of objects
 */
export const getStatusOptions = () => {
  return Object.values(UserStatus).map((status) => ({
    value: status,
    label: statusLabels[status],
    shortLabel: statusShortLabels[status],
    color: statusColors[status],
    textColor: statusTextColors[status],
    badgeVariant: statusBadgeVariants[status],
  }))
}

/**
 * Get status color for a given status (background color)
 */
export const getStatusColor = (status: UserStatus): string => {
  return statusColors[status] || statusColors[UserStatus.Offline]
}

/**
 * Get status text color for a given status
 */
export const getStatusTextColor = (status: UserStatus): string => {
  return statusTextColors[status] || statusTextColors[UserStatus.Offline]
}

/**
 * Get badge variant for a given status
 */
export const getStatusBadgeVariant = (
  status: UserStatus
): 'default' | 'secondary' | 'destructive' | 'outline' => {
  return statusBadgeVariants[status] || 'outline'
}

/**
 * Get short label for a given status
 */
export const getStatusShortLabel = (status: UserStatus): string => {
  return statusShortLabels[status] || statusLabels[status] || 'Unknown'
}

/**
 * Check if a status indicates the user is online
 */
export const isOnlineStatus = (status: UserStatus): boolean => {
  return [UserStatus.Online, UserStatus.OnlineReadyToChat, UserStatus.OnlineInDeepWork].includes(
    status
  )
}

/**
 * Check if a status indicates the user is available for communication
 */
export const isAvailableStatus = (status: UserStatus): boolean => {
  return [UserStatus.Online, UserStatus.OnlineReadyToChat].includes(status)
}

/**
 * Check if a status indicates the user should not be disturbed
 */
export const isBusyStatus = (status: UserStatus): boolean => {
  return [UserStatus.DoNotDisturb, UserStatus.Busy].includes(status)
}
